// Давность неподтверждённого подотчёта: четыре корзины по срокам.
//
// ## Какой вопрос закрывает
//
// Плитка над пультом отвечает «сколько висит»: четырнадцать миллионов. Она не отвечает на
// второй вопрос, от которого и зависит решение, — давно ли. Четырнадцать миллионов, выданных
// на этой неделе, и четырнадцать миллионов, висящих больше месяца, — это разные положения дел
// и разные действия: во втором случае деньги уже превращаются в невозвратные потери, и именно
// про них заказчик говорит «там уже миллионы исчисляются».
//
// До 08.09.2026 давность на этом экране была, но только по одному человеку за раз — строкой
// «не отчитывается 44 дня». Чтобы понять положение целиком, надо было прочитать двадцать три
// строки и сложить их в уме.
//
// ## Вывод стоит ПЕРВЫМ и назван словами
//
// Первый живой кадр показал четыре суммы и полосу, у которой один сегмент занимал девяносто
// пять процентов, а три остальных превратились в чёрточки. Формально верно, а прочитать
// нельзя: глаз идёт на самое крупное число — «13,6 млн, до 7 дней», — то есть на ту корзину,
// с которой делать ничего не надо. Решение принимают по последней, и она самая маленькая.
//
// Поэтому первой строкой стоит готовая фраза про ПОСЛЕДНЮЮ корзину: сколько денег висит
// дольше месяца и у скольких человек. Числа её подтверждают.
//
// ## Полосы здесь нет, и это решение, а не упущение
//
// Сегментная полоса отвечает на вопрос «в какой пропорции». На живых данных пропорция
// 95 : 1 : 3 : 2, то есть три сегмента из четырёх — чёрточки в две точки шириной. Полоса,
// которую нельзя разглядеть, не отвечает ни на что и при этом занимает строку и требует
// расшифровки цветом. Доля у каждой корзины написана числом рядом с суммой — этого хватает.
//
// Тревожность несёт цвет ЧИСЛА последней корзины: красный там означает ровно то же, что
// красный в строке списка — «этим надо заняться».

import type { ReactNode } from 'react';

import type { AgeBucketOut } from '@/shared/api/endpoints/accountable';
import { formatRubles } from '@/shared/money/format';
import { countLabel } from '@/shared/text/plural';
import { Metric, MetricRow } from '@/shared/ui/metric';

export interface ДавностьПодотчётаProps {
  buckets: AgeBucketOut[];
  /** Итог, в который корзины обязаны сойтись. Из него же считаются доли. */
  total: number;
  action?: ReactNode;
}

export function ДавностьПодотчёта({ buckets, total, action }: ДавностьПодотчётаProps) {
  const доля = (сумма: number) => (total > 0 ? Math.round((сумма / total) * 100) : 0);
  const давняя = buckets.find((корзина) => корзина.code === 'stale');

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex items-start justify-between gap-3'>
        <div className='flex min-w-0 flex-col gap-1'>
          <span className='text-paragraph-sm text-text-sub-600'>Сколько времени висит</span>
          {/* Ответ блока одной фразой. Про ПОСЛЕДНЮЮ корзину, а не про самую крупную: решение
              принимают по деньгам, которые протухают, а не по тем, что выданы вчера. «С этого», а
              не «с них»: у одного человека «с них» читалось ошибкой (живой проход по боевым данным
              01.10.2026: «у 1 человека — с них и начинать»), а «с него» не годится для женщины. */}
          {давняя !== undefined && (
            <p className='text-paragraph-sm text-text-strong-950'>
              {давняя.amount === 0 ? (
                'Дольше месяца не висит ничего: отчитываются вовремя.'
              ) : (
                <>
                  Дольше месяца висит{' '}
                  <span className='tabular-nums text-error-base'>
                    {formatRubles(давняя.amount)}
                  </span>{' '}
                  у {countLabel(давняя.people, ['человека', 'человек', 'человек'])} — с этого и
                  начинать.
                </>
              )}
            </p>
          )}
        </div>
        {action !== undefined && <div className='shrink-0'>{action}</div>}
      </div>

      <MetricRow columns={4}>
        {buckets.map((корзина) => (
          <Metric
            key={корзина.code}
            label={корзина.title}
            // Пустая корзина — это НОЛЬ по расчёту, а не «нет данных»: мы посчитали, и в этом
            // сроке денег не оказалось. Поэтому цифра, а не прочерк.
            value={formatRubles(корзина.amount)}
            // Красным — только последняя корзина и только когда в ней есть деньги. Цвет
            // обязан что-то означать: здесь он означает «этим надо заняться», ровно как в
            // строке списка у превышенного порога.
            tone={корзина.code === 'stale' && корзина.amount > 0 ? 'тревожный' : 'обычный'}
            note={`${доля(корзина.amount)}% · ${
              корзина.people === 0
                ? 'никого'
                : countLabel(корзина.people, ['человек', 'человека', 'человек'])
            }`}
          />
        ))}
      </MetricRow>
    </div>
  );
}
